import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import i18n from 'i18n';

import ErrorBoundary from 'layout/components/ErrorBoundary';
import PageShell from 'layout/components/PageShell';
import * as PageLayout from 'layout/styles/PageLayout.style';

import { useLogoutAllMutation } from 'auth/hooks/loginApi';
import {
  useUser,
  useUpdateIdentityMutation,
  useUpdateLanguageMutation,
} from 'shared/hooks/api/user';
import { usePosthogCapture } from 'settings/posthogCapture/_index';
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
} from 'shared/hooks/api/notificationSettings';

import Account from 'settings/components/Account';
import { SupportedLanguage } from 'i18n/languages';
import InputMethod from 'settings/components/InputMethod';
import usePushNotifications from 'pwa/hooks/usePushNotifications';
import Notifications, {
  NotificationSettings,
} from 'settings/components/Notifications';
import ShiftSchedule, {
  ShiftScheduleSettings,
} from 'settings/components/ShiftSchedule';
import * as Styled from 'settings/styles/SettingsPage.style';
import {
  InputMethodValue,
  readStoredInputMethod,
  writeStoredInputMethod,
} from 'settings/utils/inputMethodStorage';
import { normalizeTimeToHHMM } from 'settings/utils/timeFormat';
import {
  daysToIntegers,
  integersToDays,
} from 'settings/utils/weekdayConversion';

const DEFAULT_NOTIFICATIONS: NotificationSettings = {
  allowShiftNotes: true,
  allowConversations: true,
};

const DEFAULT_SHIFT_SCHEDULE: ShiftScheduleSettings = {
  autoSchedule: true,
  time: '08:00',
  days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
};

export default function SettingsPage(): JSX.Element {
  const { t } = useTranslation('pageSettings');
  const { data: user } = useUser();
  const posthog = usePostHog();
  const settings = usePosthogCapture();
  const canAccessShiftNotes = posthog.isFeatureEnabled('shift_notes');

  const [nickname, setNickname] = useState<string>(user?.nickname || '');
  const [language, setLanguage] = useState<SupportedLanguage>(
    (user?.language as SupportedLanguage) || 'en'
  );
  const [inputMethod, setInputMethod] = useState<InputMethodValue>(
    readStoredInputMethod
  );

  const changeInputMethod = (next: InputMethodValue) => {
    setInputMethod(next);
    writeStoredInputMethod(next);
  };

  const { mutate: updateIdentity } = useUpdateIdentityMutation();
  const { mutate: updateLanguage } = useUpdateLanguageMutation();
  const { mutate: logoutAllDevices } = useLogoutAllMutation();

  const { data: notificationSettingsData } = useNotificationSettings();
  const pushNotifications = usePushNotifications();
  const { mutate: updateNotificationSettings } =
    useUpdateNotificationSettings();

  useEffect(() => {
    if (user?.nickname) setNickname(user.nickname);
    if (user?.language) setLanguage(user.language as SupportedLanguage);
  }, [user?.nickname, user?.language]);

  const notificationsValue = useMemo<NotificationSettings>(() => {
    if (!notificationSettingsData) return DEFAULT_NOTIFICATIONS;

    const allowShiftNotes = notificationSettingsData.shift_reminder_subscribed;
    const allowConversations = notificationSettingsData.reminder_subscribed;

    return {
      allowShiftNotes,
      allowConversations,
    };
  }, [notificationSettingsData]);

  const shiftScheduleValue = useMemo<ShiftScheduleSettings>(() => {
    if (!notificationSettingsData) return DEFAULT_SHIFT_SCHEDULE;

    return {
      autoSchedule: notificationSettingsData.autofill_notification_schedule,
      time:
        normalizeTimeToHHMM(notificationSettingsData.shift_notification_time) ??
        DEFAULT_SHIFT_SCHEDULE.time,
      days: integersToDays(
        notificationSettingsData.shift_notification_weekdays
      ),
    };
  }, [notificationSettingsData]);

  const saveNickname = (nextNickname: string) => {
    setNickname(nextNickname);
    settings.usernameEdited();
    updateIdentity({ nickname: nextNickname, pronouns: ['-'] });
  };

  const changeLanguage = (next: SupportedLanguage) => {
    setLanguage(next);
    i18n.changeLanguage(next);
    document.documentElement.lang = next;
    updateLanguage({ language: next });
  };

  const handleNotificationsChange = (next: NotificationSettings) => {
    updateNotificationSettings({
      reminder_subscribed: next.allowConversations,
      shift_reminder_subscribed: next.allowShiftNotes,
    });
  };

  const handleLogoutAllDevices = () => {
    logoutAllDevices();
    settings.loggedOutAllDevices();
    posthog?.reset();
  };

  const handleShiftScheduleChange = (next: ShiftScheduleSettings) => {
    updateNotificationSettings({
      autofill_notification_schedule: next.autoSchedule,
      shift_notification_time: next.time,
      shift_notification_weekdays: daysToIntegers(next.days),
    });
  };

  return (
    <ErrorBoundary>
      <PageLayout.Page>
        <PageShell title={t('title')}>
          <Styled.Page>
            <Account
              email={user?.email}
              nickname={nickname}
              onSaveNickname={saveNickname}
              language={language}
              officialLanguage={user?.official_language}
              onChangeLanguage={changeLanguage}
              onSignOutAllDevices={handleLogoutAllDevices}
            />

            <InputMethod value={inputMethod} onChange={changeInputMethod} />

            <Notifications
              value={notificationsValue}
              onChange={handleNotificationsChange}
              device={{
                status: pushNotifications.status,
                isBusy: pushNotifications.isBusy,
                hasFailed: pushNotifications.hasFailed,
                onEnable: pushNotifications.enable,
                onDisable: pushNotifications.disable,
              }}
              canAccessShiftNotes={canAccessShiftNotes}
            />

            {canAccessShiftNotes && (
              <ShiftSchedule
                value={shiftScheduleValue}
                onChange={handleShiftScheduleChange}
              />
            )}

            <Styled.BottomSpacer />
          </Styled.Page>
        </PageShell>
      </PageLayout.Page>
    </ErrorBoundary>
  );
}
